import React, {FC} from "react"
import {CompanyNewsTileConfig, CompanyNewsTileType} from "@irati/types"
import Spacing from "@irati/ui/src/Spacing"
import {Col, Row} from "@irati/ui/src/Layout"
import Title from "@irati-web/fields/companyNews/Title"
import ImageUploader from "@irati-web/fields/companyNews/ImageUpload"
import VideoUrl from "@irati-web/fields/companyNews/VideoUrl"
import Url from "@irati-web/fields/companyNews/Url"
import Flex from "@irati/ui/src/Flex"
import Button, {IconTypes} from "@irati/ui/src/Button"
import Icon from "@irati/ui/src/Icon"
import {Body} from "@irati/ui/src/Typography"
import styled from "styled-components"
import {FormattedMessage, useIntl} from "@irati/utils/lang"
import NotificationCheckbox from "@irati-web/fields/companyNews/NotificationCheckbox"
import {StyledSvgCard, StyledTemplate} from "../Template/styled"
import TemplateTitle from "../Template/Title"
import TemplateFindOutMore from "../Template/FindOutMore"
import {useCultureHubStore} from "../../../stores/hooks"
import TemplateIcon from "../Template/Icon"
import {templateLibrary} from "./PickFromLibrary"
import Preview from "./Preview"
import UploadFile from "./UploadFile"

interface Props {
  companyNewsTileConfig: CompanyNewsTileConfig
  updateContent: (companyNewsTileConfig: CompanyNewsTileConfig) => void
  imageOrTemplateError?: string
  removeImageOrTemplateError: () => void
}

interface BackgroundPickerProps {
  hasError: boolean
}

const Content: FC<Props> = ({updateContent, companyNewsTileConfig, imageOrTemplateError, removeImageOrTemplateError}) => {
  const {formatMessage} = useIntl()
  const {state, dispatch} = useCultureHubStore()

  const template = templateLibrary.find((t) => t.templateIcon === companyNewsTileConfig.templateIcon)

  return (
    <Spacing paddingTop={2} paddingBottom={2}>
      {state.isPreview && <Preview companyNewsTileConfig={companyNewsTileConfig} />}

      {!state.isPreview && (
        <>
          <Row>
            <Col xs={12}>
              <Title
                onChange={(message: string) => {
                  const updatedContent = Object.assign({}, companyNewsTileConfig, {message})
                  updateContent(updatedContent)
                }}
                value={companyNewsTileConfig.message}
              />

              {companyNewsTileConfig.type === CompanyNewsTileType.Image && (
                <Url
                  onChange={(url: string) => {
                    updateContent(Object.assign({}, {...companyNewsTileConfig, url}))
                  }}
                  value={companyNewsTileConfig.url}
                />
              )}
              {companyNewsTileConfig.type === CompanyNewsTileType.Video && (
                <VideoUrl
                  onChange={(videoUrl: string) => {
                    updateContent(Object.assign({}, {...companyNewsTileConfig, videoUrl}))
                  }}
                  value={companyNewsTileConfig.videoUrl}
                />
              )}
              {companyNewsTileConfig.type === CompanyNewsTileType.File && (
                <UploadFile />
              )}
              <BackgroundPicker justifyContent="space-between" alignItems="center" hasError={!!imageOrTemplateError}>
                {template ? (
                  <DropZoneContainer>
                    <StyledSvgCard color={template?.backgroundColor} isSelected={true}>
                      <StyledTemplate>
                        <TemplateIcon icon={template?.templateIcon} />
                        {template?.templateText && <TemplateTitle templateText={template?.templateText} />}
                        <TemplateFindOutMore />
                      </StyledTemplate>
                    </StyledSvgCard>
                  </DropZoneContainer>
                ) : (
                  <DropZoneContainer>
                    <ImageUploader
                      dropzoneLabel={formatMessage({id: "platform.homepage.fields.image.dropzoneLabel"})}
                      imageUrl={companyNewsTileConfig.imageUrl}
                      onUploadSuccess={(imageUrl: string) => {
                        updateContent(
                          Object.assign(
                            {},
                            {
                              ...companyNewsTileConfig,
                              imageUrl,
                              templateIcon: "",
                              templateText: "",
                              backgroundColor: ""
                            }
                          )
                        )
                        removeImageOrTemplateError()
                      }}
                    />
                  </DropZoneContainer>
                )}

                <Flex direction="column" gap={1.5} maxWidth="183px">
                  <ImageUploader
                    isUIControlledFromOutside={true}
                    dropzoneLabel={formatMessage({id: "platform.homepage.fields.image.dropzoneLabel"})}
                    imageUrl={companyNewsTileConfig.imageUrl}
                    onUploadSuccess={(imageUrl: string) => {
                      updateContent(
                        Object.assign(
                          {},
                          {
                            ...companyNewsTileConfig,
                            imageUrl,
                            templateIcon: "",
                            templateText: "",
                            backgroundColor: ""
                          }
                        )
                      )
                      removeImageOrTemplateError()
                    }}>
                    <Button small>
                      <Icon icon={IconTypes.uploadReport} small marginRight />
                      <FormattedMessage id="culturehub.companyNews.modal.backgroundPicker.uploadThumbnailCTA" />
                    </Button>
                  </ImageUploader>
                  <Or>
                    <FormattedMessage id="platform.defaultLogin.tenantLogin.button.or" />
                  </Or>
                  <Button
                    small
                    onClick={() =>
                      dispatch({
                        type: "SET_MODAL_STATE",
                        payload: {modalState: "PickFromLibrary"}
                      })
                    }>
                    <Icon icon={IconTypes.edit} small marginRight />
                    <FormattedMessage id="culturehub.companyNews.modal.backgroundPicker.pickFromLibraryCTA" />
                  </Button>
                </Flex>
              </BackgroundPicker>
            </Col>
          </Row>
          <NotificationCheckbox />
        </>
      )}
    </Spacing>
  )
}

const BackgroundPicker = styled(Flex)<BackgroundPickerProps>`
  border-width: 2px;
  border-style: dashed;
  padding: 24px;
  border-color: ${({hasError, theme}) => (hasError ? theme.colors.error : theme.colors.greys.grey20)};
`

const DropZoneContainer = styled(Flex)`
  max-width: 260px;
  text-align: center;
  flex: 1;
`

const Or = styled(Body)`
  color: ${({theme}) => theme.colors.greys.grey50};
  text-align: center;
  text-transform: uppercase;

  :before {
    margin-right: 1rem;
  }

  :after {
    margin-left: 1rem;
  }

  :before,
  :after {
    display: inline-block;
    content: "";
    border-top: 1px solid ${({theme}) => theme.colors.greys.grey50};
    width: 70px;
    transform: translateY(-0.5rem);
  }
`

export default Content
